import { keepPreviousData, useQuery } from "@tanstack/preact-query"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import type { Log } from "../../../server/types"
import { api } from "../../api"
import { fmtDuration, fmtRelative, fmtTokens } from "../../format"
import { LogDetailDialog } from "./detail"

const PAGE_SIZE = 20

function useLogs(sessionId: string) {
  const [cursors, setCursors] = useState<number[]>([Infinity])
  const page = cursors.length - 1
  const { data, isPending, isPlaceholderData } = useQuery<Log[]>({
    queryKey: ["logs", sessionId, cursors[page]],
    queryFn: () =>
      api(
        `/logs?limit=${PAGE_SIZE}&before=${cursors[page]}${sessionId ? `&sessionId=${sessionId}` : ""}`,
      ).then((r) => r.json()),
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      (q.state.data as Log[] | undefined)?.some((l) => l.state === "pending") ? 1000 : false,
  })
  const logs = data ?? []
  const loading = isPending || isPlaceholderData

  return {
    logs,
    isPending,
    loading,
    hasMore: logs.length === PAGE_SIZE,
    page,
    prev: () => setCursors(cursors.slice(0, -1)),
    next: () => setCursors([...cursors, logs[logs.length - 1].startTime]),
  }
}

export function LogsTable(props: { sessionId?: string }) {
  const sessionId = props.sessionId ?? ""
  const { logs, isPending, loading, hasMore, page, prev, next } = useLogs(sessionId)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  return (
    <>
      <Table>
        <TableHeader>
          <TableHead>Model</TableHead>
          <TableHead>API</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Input</TableHead>
          <TableHead className="text-right">Output</TableHead>
          <TableHead className="text-right">Duration</TableHead>
          <TableHead className="text-right">Time</TableHead>
        </TableHeader>
        <TableBody>
          {isPending
            ? [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : logs.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedId(c.id)}>
                  <TableCell>{c.model}</TableCell>
                  <TableCell>{c.api}</TableCell>
                  <TableCell>
                    {c.state === "pending" ? (
                      <Badge variant="outline" className="animate-pulse">
                        pending
                      </Badge>
                    ) : c.state !== "complete" ? (
                      <Badge variant="destructive" title={c.error}>
                        {c.state}
                      </Badge>
                    ) : (
                      <Badge variant={c.status! < 400 ? "secondary" : "destructive"}>
                        {c.status}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.usage && (
                      <>
                        {fmtTokens(c.usage.input - c.usage.cacheRead)}
                        {c.usage.cacheRead > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({fmtTokens(c.usage.cacheRead)})
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.usage && fmtTokens(c.usage.output)}
                  </TableCell>
                  <TableCell className="text-right">
                    {fmtDuration((c.endTime ?? Date.now()) - c.startTime)}
                  </TableCell>
                  <TableCell className="text-right" title={new Date(c.startTime).toLocaleString()}>
                    {fmtRelative(c.startTime)}
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <Pagination className="mt-1 justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              aria-disabled={loading || page === 0}
              className={loading || page === 0 ? "pointer-events-none opacity-50" : undefined}
              onClick={prev}
            />
          </PaginationItem>
          <PaginationItem className="text-sm text-muted-foreground px-1">{page + 1}</PaginationItem>
          <PaginationItem>
            <PaginationNext
              aria-disabled={loading || !hasMore}
              className={loading || !hasMore ? "pointer-events-none opacity-50" : undefined}
              onClick={next}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      <LogDetailDialog id={selectedId} onClose={() => setSelectedId(null)} />
    </>
  )
}
